import { matchesPathSegments } from './navMatch';

/**
 * v2 app shell rules (slice 0S, mock built-v12 `.side` / `.topbar` / `.page`).
 * Pure helpers so the frame's route behaviour is testable without a DOM.
 */

/** The v2 page routes: their own `.page` governs padding and ground. */
export const V2_FRAME_ROUTES = ['/home', '/sales', '/products', '/returns', '/orders', '/finances', '/reports', '/settings', '/notifications'] as const;

/**
 * Pages the mock's Settings covers (Profile, Team, Notifications, Data exports tabs).
 * `/notifications` is NOT here: it is its own v2 page (notifications mock), named
 * "Notifications" in the top bar; Settings > Notifications is the email-switch tab.
 */
export const SETTINGS_ROUTES = ['/settings', '/profile', '/team', '/company/exports'] as const;

/**
 * The path the shell reasons about. DEV preview routes (`/dev/sales`) act as
 * their real page (`/sales`), so a page slice's preview sits in the true frame
 * with the right active entry and breadcrumb; the empty frame at
 * `/dev/frame/<page>` acts as `<page>` too. Prod paths pass through.
 */
export function shellPath(pathname: string, dev: boolean = import.meta.env.DEV): string {
  return dev ? pathname.replace(/^\/dev(\/frame)?(?=\/)/, '') : pathname;
}

export function isV2FrameRoute(pathname: string): boolean {
  return V2_FRAME_ROUTES.some((r) => matchesPathSegments(pathname, r));
}

export function isSettingsRoute(pathname: string): boolean {
  return SETTINGS_ROUTES.some((r) => matchesPathSegments(pathname, r));
}

/** v1 frame classes, verbatim (ProtectedRoute before 0S). */
export const V1_MAIN_CLASS = 'relative flex-1 overflow-y-auto overflow-x-hidden bg-gray-100 p-4 lg:p-6';
/** v2 page routes: no frame padding, the mock's page ground (`--page`) and its antialiased text (`html{-webkit-font-smoothing}`). */
export const V2_PAGE_MAIN_CLASS = 'relative flex-1 overflow-y-auto overflow-x-hidden bg-page antialiased';

/**
 * V2 pages every user reaches, v1 shell included: the shipment notice pages
 * (role-gated, not preview-gated). Their V2Page owns padding and ground, so
 * the v1 frame's padding would double it. Not in V2_FRAME_ROUTES: that list
 * is the v2 shell's own (e2e/v2-routes.spec.ts reads it).
 */
export const V2_PAGES_FOR_ALL = ['/shipments'] as const;

/** The `<main>` classes for a route. v1 users get the v1 frame, except on V2_PAGES_FOR_ALL. */
export function frameMainClass(v2: boolean, pathname: string): string {
  const path = shellPath(pathname);
  if (V2_PAGES_FOR_ALL.some((r) => matchesPathSegments(path, r))) return V2_PAGE_MAIN_CLASS;
  return v2 && isV2FrameRoute(path) ? V2_PAGE_MAIN_CLASS : V1_MAIN_CLASS;
}

/** Top-bar page names in v2 mode ("Ivonne · Sales"). */
const PAGE_NAMES: Record<string, string> = {
  '/home': 'Home',
  '/sales': 'Sales',
  '/products': 'Products',
  '/returns': 'Returns',
  '/orders': 'Orders',
  '/finances': 'Finances',
  '/reports': 'Reports & downloads',
  '/notifications': 'Notifications',
  '/catalog': 'Catalog',
  '/admin': 'Admin',
};

export function v2PageName(pathname: string): string | null {
  if (isSettingsRoute(pathname)) return 'Settings';
  const hit = Object.keys(PAGE_NAMES).find((r) => matchesPathSegments(pathname, r));
  return hit ? PAGE_NAMES[hit] : null;
}

/** Brand initials for the sidebar brand block: "Ivonne" → "IV", "Ronen Chen" → "RC". */
export function brandInitials(name: string | null | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const raw = words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0];
  return raw.toLocaleUpperCase();
}

/**
 * The rail is 72px above 860px (the mock's breakpoint), a drawer below it.
 * A sidebar label: hidden in the 72px rail, shown when the rail opens on
 * hover or keyboard focus (`.side .lbl`), always shown in the phone drawer.
 */
export const SIDE_LABEL =
  'transition-opacity duration-[120ms] min-[861px]:opacity-0 min-[861px]:group-hover/side:opacity-100 min-[861px]:group-focus-within/side:opacity-100';
